import moment from 'moment-timezone';
import { cancelled_status } from '../../../../constants';
import {
  IST,
  KIND_CANCEL,
  cancel_initiator,
  covers_care,
  has_errors,
  needs_cancel_capture,
  teacher_note_of,
  validate_care_draft
} from '../../../util_components/care_request/care_request_rules';
import {
  is_valid_reason,
  trim_reason
} from '../../../util_components/required_reason_textarea/reason_rules';
import {
  derive_cancel_bucket,
  is_requested_at_in_future,
  requested_at_to_iso
} from '../../../util_components/teacher_cancel_bucket_picker/bucket_rules';
import { flag_on, resolve_edit_reason } from './edit_reason_rules';

// Pure rules behind the session page's Edit, Change teacher and Change time modals; the components only do I/O.

const NO_EDIT_ERRORS = {
  rating_error: false,
  status_reason_error: false,
  cancel_bucket_error: false,
  save_modal_error: ''
};

// A cancelled session opens with its saved "who cancelled" ticked; the radios are exclusive, teacher wins a stale pair.
export function saved_cancel_flags(session_details) {
  const cancelled = cancelled_status.includes(session_details.session_status);
  const by_teacher = cancelled && flag_on(session_details.cancelled_by_teacher);
  return {
    cancelled_by_student: cancelled && !by_teacher && flag_on(session_details.cancelled_by_student),
    cancelled_by_teacher: by_teacher
  };
}

// The Edit modal opens on the loaded session's values, so an abandoned edit never leaks.
export const edit_form_from = (session_details) => ({
  ...NO_EDIT_ERRORS,
  status: session_details.session_status,
  ...saved_cancel_flags(session_details),
  status_change_reason: '',
  teacher_cancel_requested_at: '',
  cancel_pick: '',
  cancel_initiator_error: false
});

// Any typed field clears the save errors; leaving CANCELLED drops who cancelled and the teacher's request time.
export const edit_field_patch = (name, value) => ({
  ...(name === 'status' && value !== 'CANCELLED'
    ? {
        cancelled_by_student: false,
        cancelled_by_teacher: false,
        cancel_pick: '',
        teacher_cancel_requested_at: ''
      }
    : {}),
  [name]: value,
  ...NO_EDIT_ERRORS
});

export const ignore_reason_patch = () => ({ ...NO_EDIT_ERRORS });

// The two cancel radios are exclusive; picking one cancels the session. The request time only applies to the teacher.
export function toggle_cancel_patch(form, option) {
  const cleared = {
    cancel_initiator_error: false,
    rating_error: false,
    cancel_bucket_error: false
  };
  if (option === 'cancelled_by_student') {
    const next_by_teacher = form.cancelled_by_student;
    return {
      cancelled_by_student: !form.cancelled_by_student,
      cancelled_by_teacher: next_by_teacher,
      ...cleared,
      ...(next_by_teacher ? {} : { teacher_cancel_requested_at: '' }),
      status: form.cancelled_by_student ? form.status : 'CANCELLED'
    };
  }
  if (option === 'cancelled_by_teacher') {
    const next_by_teacher = !form.cancelled_by_teacher;
    return {
      cancelled_by_teacher: next_by_teacher,
      cancelled_by_student: form.cancelled_by_teacher,
      ...cleared,
      ...(next_by_teacher ? {} : { teacher_cancel_requested_at: '' }),
      status: form.cancelled_by_teacher ? form.status : 'CANCELLED'
    };
  }
  return {};
}

// Cancelling a live 1-on-1 records Care's "who asked, and when".
export const is_cancel_capture = (status, session_details, care_draft) =>
  !!care_draft &&
  needs_cancel_capture({
    status,
    original_was_cancelled: cancelled_status.includes(session_details.session_status),
    session_type: session_details.session_type
  });

// A teacher cancel is new only against the saved flag (apis is_new_teacher_cancel); only then is its time required.
export const is_new_teacher_cancel = (form, session_details) =>
  !!form.cancelled_by_teacher && !flag_on(session_details.cancelled_by_teacher);

// Re-saving the saved teacher cancel with a typed time corrects it; blank keeps the stored time.
export const is_teacher_time_correction = (form, session_details, care_owns_time = false) =>
  form.status === 'CANCELLED' &&
  !care_owns_time &&
  !!form.cancelled_by_teacher &&
  !is_new_teacher_cancel(form, session_details) &&
  !!form.teacher_cancel_requested_at;

// Which cancel flags the save sends: only a newly ticked one, or the teacher flag carrying a time correction.
export function cancel_flags_to_send(form, session_details, care_owns_time = false) {
  const student = !!form.cancelled_by_student && !flag_on(session_details.cancelled_by_student);
  const teacher =
    is_new_teacher_cancel(form, session_details) ||
    is_teacher_time_correction(form, session_details, care_owns_time);
  return { student, teacher, time: teacher && !care_owns_time };
}

// The cancelled_by_teacher value update_session_payload sends; undefined = not sent, apis keeps the stored flag.
export const teacher_flag_sent = (send) => {
  if (send.teacher) return 1;
  if (send.student) return 0;
  return undefined;
};

// Group class rows judge the note against the flags their pick sends, as the group class page does.
const group_edit_reason = (form, session_details) =>
  resolve_edit_reason({
    status: form.status,
    original_status: session_details.session_status,
    original_cancelled_by_student: session_details.cancelled_by_student,
    original_cancelled_by_teacher: session_details.cancelled_by_teacher,
    sent_flags: group_cancel_flags_sent(form.status === 'CANCELLED' ? form.cancel_pick : ''),
    teacher_time_corrected:
      form.cancel_pick === GROUP_CANCEL_TEACHER && flag_on(session_details.cancelled_by_teacher),
    status_change_reason: form.status_change_reason
  });

export const edit_reason_of = (form, session_details, care_owns_time = false) =>
  uses_group_cancel_picks(session_details.session_type)
    ? group_edit_reason(form, session_details)
    : resolve_edit_reason({
        status: form.status,
        original_status: session_details.session_status,
        cancelled_by_student: form.cancelled_by_student,
        cancelled_by_teacher: form.cancelled_by_teacher,
        original_cancelled_by_student: session_details.cancelled_by_student,
        original_cancelled_by_teacher: session_details.cancelled_by_teacher,
        teacher_time_corrected: is_teacher_time_correction(form, session_details, care_owns_time),
        status_change_reason: form.status_change_reason
      });

// A new teacher cancel needs the request time; goserver derives the category from it. A re-save checks a
// typed time only. Capture mode's Care draft already rejects a future time, so only the picker's field is checked here.
export function teacher_cancel_check({
  status,
  cancelled_by_teacher,
  epoch_start_time,
  requested_local,
  capture = false,
  now_ms,
  new_cancel = true
}) {
  const bucket = cancelled_by_teacher
    ? derive_cancel_bucket(epoch_start_time, requested_local, IST)
    : '';
  const missing =
    status === 'CANCELLED' &&
    cancelled_by_teacher &&
    (new_cancel || !!requested_local) &&
    (!bucket || (!capture && is_requested_at_in_future(requested_local, IST, now_ms)));
  return { bucket, missing };
}

// Mirrors goserver carereq cancelledStatuses: the statuses a Care cancellation record can belong to.
const CARE_CANCELLED_STATUSES = [
  'CANCELLED',
  'CANCELLED_BY_TEACHER',
  'CANCELLED_BY_ADMIN',
  'CANCELLED_BY_STUDENT',
  'NO_SHOW_CANCELLATION_1HOUR',
  'JOINED_BUT_CANCELLED'
];

// Owner rule (A-52): no payment wording on session screens; only where the time lives.
export const CARE_OWNS_CANCEL_TIME_NOTE =
  "This cancellation's request time is kept on its Care record. To correct it, edit 'Requested at' on the Cancellation under Change requests.";

// Only a session already cancelled by the teacher can have its request time re-saved from Edit.
export const may_have_care_cancel_time = (session_details) =>
  parseInt(session_details.cancelled_by_teacher, 10) === 1 &&
  CARE_CANCELLED_STATUSES.includes(session_details.session_status) &&
  covers_care(KIND_CANCEL, session_details.session_type);

// The Care record made for the session's current cancellation (same second), or null; goserver prices its
// request time over the one Edit stores, as in factsmap careCancel.
export function current_care_cancel_record(care_view, session_status) {
  if (!care_view || !CARE_CANCELLED_STATUSES.includes(session_status)) return null;
  const change = (care_view.changes || []).find(
    (c) => c.change_kind === KIND_CANCEL && c.record_uuid
  );
  const record = change && (care_view.records || []).find((r) => r.uuid === change.record_uuid);
  if (!record) return null;
  const apart_ms = Math.abs(
    moment(record.change_made_at).valueOf() - moment(change.change_made_at).valueOf()
  );
  return apart_ms < 1000 ? record : null;
}

// Re-saving a teacher cancel whose time a current Care record sets: Edit neither asks for nor sends a time.
export const care_owns_cancel_time = (form, session_details, care_record) =>
  !!care_record &&
  form.status === 'CANCELLED' &&
  !!form.cancelled_by_teacher &&
  parseInt(session_details.cancelled_by_teacher, 10) === 1;

// A cancel must name who asked: an admin-only cancel cannot be priced (teacher payments CANCEL_ADMIN_ONLY).
export const cancel_initiator_missing = (status, cancelled_by_student, cancelled_by_teacher) =>
  status === 'CANCELLED' && !cancelled_by_student && !cancelled_by_teacher;

// Group class page "who cancelled" picks. A GC booking and the group class itself offer Admin or Teacher only
// (PROD-7828: Care never marks them student-cancelled; an admin-only class pays the teacher 0); other rows keep Student or Teacher.
export const GROUP_CANCEL_ADMIN = 'ADMIN';
export const GROUP_CANCEL_TEACHER = 'TEACHER';
export const GROUP_CANCEL_STUDENT = 'STUDENT';
export const GROUP_CANCEL_LABELS = {
  [GROUP_CANCEL_ADMIN]: 'Cancelled by Admin',
  [GROUP_CANCEL_TEACHER]: 'Cancelled by Teacher',
  [GROUP_CANCEL_STUDENT]: 'Cancelled by Student'
};
export const STUDENT_APP_CANCEL_NOTE = 'Cancelled by the student in the app';
export const STORED_STUDENT_CANCEL_NOTE = 'Saved as Cancelled by Student';

const GROUP_CLASS_PARENT_TYPES = ['GROUP_SESSION', 'PRIVATE_GROUP_SESSION'];
const ADMIN_OR_TEACHER_TYPES = ['GROUP_SESSION_STUDENT', ...GROUP_CLASS_PARENT_TYPES];

// A GC booking or group class edited from the session page uses the group page's picks, not the 1-on-1 radios.
export const uses_group_cancel_picks = (session_type) =>
  ADMIN_OR_TEACHER_TYPES.includes(session_type);

export const group_cancel_options = (session_type) =>
  ADMIN_OR_TEACHER_TYPES.includes(session_type)
    ? [GROUP_CANCEL_ADMIN, GROUP_CANCEL_TEACHER]
    : [GROUP_CANCEL_STUDENT, GROUP_CANCEL_TEACHER];

// The flags a pick sends, always naming one side so a stale opposite flag is cleared; no pick sends neither and
// apis keeps the stored flags (absent is a no-op there, 0 overwrites).
export function group_cancel_flags_sent(cancel_pick) {
  if (cancel_pick === GROUP_CANCEL_TEACHER) return { student: 0, teacher: 1 };
  if (cancel_pick === GROUP_CANCEL_ADMIN) return { student: 0, teacher: 0 };
  if (cancel_pick === GROUP_CANCEL_STUDENT) return { student: 1, teacher: 0 };
  return { student: undefined, teacher: undefined };
}

// Clicking the picked option again clears it; any pick cancels the session.
export const group_cancel_pick_patch = (form, option) => {
  const next = form.cancel_pick === option ? '' : option;
  return {
    cancel_pick: next,
    status: next ? 'CANCELLED' : form.status,
    ...(next === GROUP_CANCEL_TEACHER ? {} : { teacher_cancel_requested_at: '' }),
    cancel_bucket_error: false,
    cancel_initiator_error: false
  };
};

// A CANCELLED save must name a side, unless the saved row already does (re-save keeps the stored flags).
export const group_cancel_initiator_missing = (form, session_status, raw = {}) => {
  const saved = saved_cancel_flags({
    session_status,
    cancelled_by_student: raw.cancelled_by_student,
    cancelled_by_teacher: raw.cancelled_by_teacher
  });
  return (
    form.status === 'CANCELLED' &&
    !form.cancel_pick &&
    !saved.cancelled_by_student &&
    !saved.cancelled_by_teacher
  );
};

// A stored student cancel on a row with no Student option stays as read-only text until Care picks a side.
export const shows_student_app_cancel = (form, session_type, session_status, raw = {}) =>
  ADMIN_OR_TEACHER_TYPES.includes(session_type) &&
  form.status === 'CANCELLED' &&
  !form.cancel_pick &&
  saved_cancel_flags({
    session_status,
    cancelled_by_student: raw.cancelled_by_student,
    cancelled_by_teacher: raw.cancelled_by_teacher
  }).cancelled_by_student;

// A booking's student flag comes from the app; a group class's was saved by an earlier Care edit.
export const stored_student_cancel_note = (session_type) =>
  GROUP_CLASS_PARENT_TYPES.includes(session_type)
    ? STORED_STUDENT_CANCEL_NOTE
    : STUDENT_APP_CANCEL_NOTE;

// Group row Edit save: same checks and order as the group class page's save_modal.
function plan_group_edit_save({
  form,
  session_details,
  ignore_rating,
  ignore_rating_reason,
  now_ms
}) {
  const edit = group_edit_reason(form, session_details);
  const { missing } = teacher_cancel_check({
    status: form.status,
    cancelled_by_teacher: form.cancel_pick === GROUP_CANCEL_TEACHER,
    epoch_start_time: session_details.epoch_start_time,
    requested_local: form.teacher_cancel_requested_at,
    now_ms
  });
  if (group_cancel_initiator_missing(form, session_details.session_status, session_details)) {
    return { errors: { cancel_initiator_error: true } };
  }
  if (ignore_rating && !ignore_rating_reason) return { errors: { rating_error: true } };
  if (!edit.reason_valid) return { errors: { status_reason_error: true } };
  if (missing) return { errors: { cancel_bucket_error: true } };
  return { edit, capture: false, group: true };
}

// The Edit save, decided: either the state patch that shows the first problem, or what the save sends.
export function plan_edit_save({
  form,
  session_details,
  care_draft,
  ignore_rating,
  ignore_rating_reason,
  now_ms,
  care_record = null
}) {
  if (uses_group_cancel_picks(session_details.session_type)) {
    return plan_group_edit_save({
      form,
      session_details,
      ignore_rating,
      ignore_rating_reason,
      now_ms
    });
  }
  const capture = is_cancel_capture(form.status, session_details, care_draft);
  const care_owns_time = care_owns_cancel_time(form, session_details, care_record);
  const edit = edit_reason_of(form, session_details, care_owns_time);
  const send = cancel_flags_to_send(form, session_details, care_owns_time);
  const care_errors = capture
    ? validate_care_draft(care_draft, { now_ms, require_initiated_by: false })
    : {};
  const initiator = cancel_initiator(form.cancelled_by_teacher, form.cancelled_by_student);
  const requested_local = capture ? care_draft.requested_at : form.teacher_cancel_requested_at;
  const { missing } = teacher_cancel_check({
    status: form.status,
    cancelled_by_teacher: form.cancelled_by_teacher && !care_owns_time,
    epoch_start_time: session_details.epoch_start_time,
    requested_local,
    capture,
    now_ms,
    new_cancel: capture || is_new_teacher_cancel(form, session_details)
  });
  if (capture && (!initiator || has_errors(care_errors))) {
    return {
      errors: { cancel_initiator_error: !initiator, cancel_bucket_error: missing },
      care_errors
    };
  }
  if (cancel_initiator_missing(form.status, form.cancelled_by_student, form.cancelled_by_teacher)) {
    return { errors: { cancel_initiator_error: true } };
  }
  if (ignore_rating && !ignore_rating_reason) return { errors: { rating_error: true } };
  if (!edit.reason_valid) return { errors: { status_reason_error: true } };
  if (missing) return { errors: { cancel_bucket_error: true } };
  return { edit, capture, initiator, requested_local, care_owns_time, send };
}

function group_session_edit_payload({
  session_uuid,
  student_uuid,
  form,
  plan,
  ignore_rating,
  ignore_rating_reason
}) {
  const payload = group_update_session_payload({
    session_uuid,
    student_uuid,
    status: form.status,
    edit: plan.edit,
    cancel_pick: form.cancel_pick,
    teacher_cancel_requested_at: form.teacher_cancel_requested_at
  });
  if (ignore_rating) {
    payload.ignore_rating = 1;
    payload.ignore_rating_reason = ignore_rating_reason.slice(0, 255);
  }
  return payload;
}

export function update_session_payload({
  session_uuid,
  student_uuid,
  form,
  plan,
  ignore_rating,
  ignore_rating_reason
}) {
  // No penalty fields: apis keeps the stored penalty and saves the status-change reason itself.
  if (plan.group)
    return group_session_edit_payload({
      session_uuid,
      student_uuid,
      form,
      plan,
      ignore_rating,
      ignore_rating_reason
    });
  const payload = { session_uuid, status: form.status, student_uuid };
  if (plan.edit.reason_required) payload.reason = plan.edit.reason;
  if (form.status === 'CANCELLED') payload.cancelled_by_admin = 1;
  if (ignore_rating) {
    payload.ignore_rating = 1;
    payload.ignore_rating_reason = ignore_rating_reason.slice(0, 255);
  }
  // An unchanged saved flag is not re-sent: apis would re-run its cancel side effects (e.g. charge_cancellation).
  if (plan.send.student) {
    payload.cancelled_by_student = 1;
    payload.cancelled_by_admin = 1;
    payload.cancelled_by_teacher = 0;
  }
  if (plan.send.teacher) {
    payload.cancelled_by_teacher = 1;
    payload.cancelled_by_admin = 1;
    payload.cancelled_by_student = 0;
    if (plan.send.time) {
      payload.teacher_cancel_requested_at = requested_at_to_iso(plan.requested_local, IST);
    }
  }
  return payload;
}

// Group class page's Edit save.
export function group_update_session_payload({
  session_uuid,
  student_uuid,
  status,
  edit,
  cancel_pick,
  teacher_cancel_requested_at
}) {
  // No penalty fields: apis keeps the stored penalty and saves the status-change reason itself.
  const payload = { session_uuid, status, student_uuid };
  if (edit.reason_required) payload.reason = edit.reason;
  if (status === 'CANCELLED') payload.cancelled_by_admin = 1;
  if (status !== 'CANCELLED' || !cancel_pick) return payload;
  const sent = group_cancel_flags_sent(cancel_pick);
  payload.cancelled_by_student = sent.student;
  payload.cancelled_by_teacher = sent.teacher;
  if (cancel_pick === GROUP_CANCEL_TEACHER) {
    payload.teacher_cancel_requested_at = requested_at_to_iso(teacher_cancel_requested_at, IST);
  }
  return payload;
}

// update_session refusals carry the actionable text (e.g. the slot is taken) in reason, exception_message or message.
export const update_session_error_text = (e, fallback) => {
  const d = e?.response?.data;
  return (
    (typeof d?.reason === 'string' && d.reason) ||
    (typeof d?.exception_message === 'string' && d.exception_message) ||
    (typeof d?.message === 'string' && d.message) ||
    fallback
  );
};

export const teacher_options_of = (teachers) =>
  teachers.map((teacher) => ({
    value: teacher.teacher_uuid,
    label: `${teacher.name} ${
      teacher.is_consultant_therapist_in_preferred_group == 1 ? ' (preferred consultant)' : ''
    }`
  }));

// Change teacher: the internal note (apis wants 3+ chars), then Care's details, then a teacher.
export function plan_teacher_change({ change_reason, care_draft, teacher_uuid, now_ms }) {
  const reason = trim_reason(change_reason);
  if (!is_valid_reason(reason)) return { reason_error: true };
  const care_errors = care_draft ? validate_care_draft(care_draft, { now_ms }) : {};
  if (has_errors(care_errors)) return { care_errors };
  if (!teacher_uuid) return { teacher_missing: true };
  return { reason };
}

// The teacher email shows only the note for the teacher, never the internal reason.
export function teacher_change_payload({ session_uuid, teacher_uuid, reason, care_draft }) {
  const payload = { session_uuid, teacher_uuid, reason };
  const teacher_note = teacher_note_of(care_draft);
  if (teacher_note) payload.teacher_note = teacher_note;
  return payload;
}

// Change time: the note is required for the payment audit, then Care's details.
export function plan_time_change({ time_change_reason, care_draft, now_ms }) {
  const reason = trim_reason(time_change_reason);
  if (!is_valid_reason(reason)) return { reason_error: true };
  const care_errors = care_draft ? validate_care_draft(care_draft, { now_ms }) : {};
  if (has_errors(care_errors)) return { care_errors };
  return { reason };
}

// The request time this Edit save sends ('' = none), for the A-52 request-time pre-check: a group
// teacher pick's time, the Care draft's time on a cancel capture, else a 1-on-1 teacher-cancel time.
export function sent_request_time({ form, plan, care_draft, session_type }) {
  if (form.status !== 'CANCELLED') return '';
  if (uses_group_cancel_picks(session_type)) {
    return form.cancel_pick === GROUP_CANCEL_TEACHER ? form.teacher_cancel_requested_at || '' : '';
  }
  if (plan.capture) return (care_draft && care_draft.requested_at) || '';
  return plan.send && plan.send.teacher && plan.send.time ? plan.requested_local || '' : '';
}
